import React, { useState, useEffect } from 'react';
import {
    MapPin,
    Plus,
    Search,
    Eye,
    Edit,
    Trash2
} from 'lucide-react';
import Button from '../common/Button';
import Modal from '../common/Modal';
import Loading from '../common/Loading';
import { ubicacionesService } from '../../services/ubicaciones';
import toast from 'react-hot-toast';


const UbicacionesList = () => {
    const [ubicaciones, setUbicaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('create');
    const [selectedUbicacion, setSelectedUbicacion] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: ''
    });

    // Cargar ubicaciones al montar el componente
    useEffect(() => {
        loadUbicaciones();
    }, []);

    // Cargar ubicaciones desde la API
    const loadUbicaciones = async (search = '') => {
        try {
            setLoading(true);
            const params = search ? { search } : {};
            const response = await ubicacionesService.getUbicaciones(params);
            setUbicaciones(response.ubicaciones || response);
        } catch (error) {
            console.error('Error al cargar ubicaciones:', error);
            toast.error('Error al cargar ubicaciones');
        } finally {
            setLoading(false);
        }
    };

    // Buscar ubicaciones
    const handleSearch = async () => {
        await loadUbicaciones(searchTerm);
    };

    // Búsqueda con delay
    useEffect(() => {
        const delayedSearch = setTimeout(() => {
            if (searchTerm !== '') {
                handleSearch();
            } else {
                loadUbicaciones();
            }
        }, 500);

        return () => clearTimeout(delayedSearch);
    }, [searchTerm]);

    // Crear ubicación
    const handleCreate = () => {
        setModalType('create');
        setFormData({
            nombre: '',
            descripcion: ''
        });
        setShowModal(true);
    };

    // Editar ubicación
    const handleEdit = (ubicacion) => {
        setModalType('edit');
        setSelectedUbicacion(ubicacion);
        setFormData({
            nombre: ubicacion.nombre,
            descripcion: ubicacion.descripcion || ''
        });
        setShowModal(true);
    };

    // Ver detalles
    const handleView = async (ubicacion) => {
        setModalType('view');
        setSelectedUbicacion(ubicacion);
        setShowModal(true);
    };

    // Eliminar ubicación
    const handleDelete = async (ubicacion) => {
        if (window.confirm(`¿Estás seguro de eliminar la ubicación "${ubicacion.nombre}"?`)) {
            try {
                await ubicacionesService.deleteUbicacion(ubicacion.id);
                toast.success('Ubicación eliminada correctamente');
                loadUbicaciones();
            } catch (error) {
                console.error('Error al eliminar ubicación:', error);
                const errorMessage = error.response?.data?.detail || 'Error al eliminar ubicación';
                toast.error(errorMessage);
            }
        }
    };


    // Guardar ubicación (crear o editar)
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // Validaciones básicas
            if (!formData.nombre.trim()) {
                toast.error('El nombre es obligatorio');
                return;
            }

            const submitData = {
                ...formData
            };

            if (modalType === 'create') {
                await ubicacionesService.createUbicacion(submitData);
                toast.success('Ubicación creada correctamente');
            } else if (modalType === 'edit') {
                await ubicacionesService.updateUbicacion(selectedUbicacion.id, submitData);
                toast.success('Ubicación actualizada correctamente');
            }

            setShowModal(false);
            loadUbicaciones();
        } catch (error) {
            console.error('Error al guardar ubicación:', error);
            const errorMessage = error.response?.data?.detail || 'Error al guardar ubicación';
            toast.error(errorMessage);
        }
    };


    if (loading) {
        return <Loading message="Cargando ubicaciones..." />;
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
                <div className="flex items-center space-x-3 mb-4 md:mb-0">
                    <MapPin className="h-8 w-8 text-blue-600" />
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Gestión de Ubicaciones</h1>
                        <p className="text-gray-600">Administra las ubicaciones de tu empresa</p>
                    </div>
                </div>

                <div className="flex space-x-3">
                    <Button onClick={handleCreate} className="flex items-center space-x-2">
                        <Plus className="h-4 w-4" />
                        <span>Nueva Ubicación</span>
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Buscar ubicaciones por nombre, código o descripción..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-8">
                <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center">
                        <MapPin className="h-8 w-8 text-blue-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total Ubicaciones</p>
                            <p className="text-2xl font-bold text-gray-900">{ubicaciones.length}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Ubicaciones Table */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                        Lista de Ubicaciones ({ubicaciones.length})
                    </h2>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Nombre
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Descripción
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {ubicaciones.map((ubicacion) => (
                                <tr key={ubicacion.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center">
                                            <MapPin className="h-5 w-5 text-gray-400 mr-2" />
                                            <div className="text-sm font-medium text-gray-900">
                                                {ubicacion.nombre}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-gray-500">
                                            {ubicacion.descripcion || 'Sin descripción'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => handleView(ubicacion)}
                                                className="text-blue-600 hover:text-blue-900"
                                                title="Ver detalles"
                                            >
                                                <Eye className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => handleEdit(ubicacion)}
                                                className="text-green-600 hover:text-green-900"
                                                title="Editar"
                                            >
                                                <Edit className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(ubicacion)}
                                                className="text-red-600 hover:text-red-900"
                                                title="Eliminar"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {ubicaciones.length === 0 && (
                        <div className="text-center py-8">
                            <MapPin className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No hay ubicaciones</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                {searchTerm ? 'No se encontraron ubicaciones con esa búsqueda' : 'Comienza creando una nueva ubicación'}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal para Crear/Editar/Ver Ubicación */}
            {showModal && (
                <Modal
                    title={
                        modalType === 'create' ? 'Nueva Ubicación' :
                            modalType === 'edit' ? 'Editar Ubicación' :
                                'Detalles de la Ubicación'
                    }
                    onClose={() => setShowModal(false)}
                    size="md"
                >
                    {modalType === 'view' ? (
                        <UbicacionDetails ubicacion={selectedUbicacion} />
                    ) : (
                        <UbicacionForm
                            formData={formData}
                            setFormData={setFormData}
                            onSubmit={handleSubmit}
                            isEdit={modalType === 'edit'}
                            onClose={() => setShowModal(false)}
                        />
                    )}
                </Modal>
            )}
        </div>
    );
};

// Componente para el formulario
const UbicacionForm = ({ formData, setFormData, onSubmit, isEdit, onClose }) => {
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    return (
        <form onSubmit={onSubmit} className="space-y-6">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ej: Oficina Principal"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descripción
                </label>
                <textarea
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Descripción opcional de la ubicación..."
                />
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t">
                <Button type="button" variant="outline" onClick={onClose}>
                    Cancelar
                </Button>
                <Button type="submit">
                    {isEdit ? 'Actualizar' : 'Crear'} Ubicación
                </Button>
            </div>
        </form>
    );
};

// Componente para mostrar detalles
const UbicacionDetails = ({ ubicacion }) => {
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
                <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Información General</h3>
                    <dl className="space-y-3">
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Nombre</dt>
                            <dd className="text-sm text-gray-900 flex items-center">
                                <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                                {ubicacion.nombre}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Descripción</dt>
                            <dd className="text-sm text-gray-900">{ubicacion.descripcion || 'Sin descripción'}</dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Fecha de Creación</dt>
                            <dd className="text-sm text-gray-900">
                                {formatDate(ubicacion.created_at)}
                            </dd>
                        </div>
                        {ubicacion.updated_at && (
                            <div>
                                <dt className="text-sm font-medium text-gray-500">Última Actualización</dt>
                                <dd className="text-sm text-gray-900">
                                    {formatDate(ubicacion.updated_at)}
                                </dd>
                            </div>
                        )}
                    </dl>
                </div>
            </div>
        </div>
    );
};

export default UbicacionesList;